import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { Driver } from '../../fleet/driver/driver.model';
import { DriverService } from '../../fleet/driver/driver.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import {
  HR_QUALIFICATION_STATUSES,
  HR_READINESS_ASSIGNMENT_TYPES,
  HR_READINESS_SEVERITIES,
  HR_READINESS_STATUSES,
  HrEmployee,
  HrDriverQualification,
  HrReadinessOverride,
  HrWorkforceReadinessDashboard,
  HrWorkforceReadinessEvaluationRequest,
  HrWorkforceReadinessEvaluationResult,
  HrWorkforceReadinessRule,
  HrWorkforceReadinessRuleResult,
  HrWorkforceReadinessSnapshot
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-workforce-readiness',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-workforce-readiness.component.html',
  styleUrls: ['./hr-workforce-readiness.component.scss']
})
export class HrWorkforceReadinessComponent implements OnInit {
  activeListTab = 'snapshots';
  private readonly hrService = inject(HrService);
  private readonly driverService = inject(DriverService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  dashboard: HrWorkforceReadinessDashboard = {};
  evaluationResult?: HrWorkforceReadinessEvaluationResult;
  filterAssignmentType?: string;
  filterReadinessStatus?: string;
  overrideValidUntilLocal = this.defaultOverrideUntil();
  snapshotDialogVisible = false;
  activeDialog?: 'evaluation' | 'rule' | 'override' | 'qualification';
  selectedSnapshot?: HrWorkforceReadinessSnapshot;
  employees: HrEmployee[] = [];
  drivers: Driver[] = [];

  evaluationRequest: HrWorkforceReadinessEvaluationRequest = this.emptyEvaluation();
  ruleForm: HrWorkforceReadinessRule = this.emptyRule();
  overrideForm: HrReadinessOverride = this.emptyOverride();
  qualificationForm: HrDriverQualification = this.emptyQualification();

  readonly canManageReadiness = this.permissionService.hasPermission(app_permissions.hr.readiness.manage);
  readonly canOverrideReadiness = this.permissionService.hasPermission(app_permissions.hr.readiness.override);
  readonly canManageQualifications = this.permissionService.hasPermission(app_permissions.hr.qualifications.manage);

  readonly assignmentOptions = HR_READINESS_ASSIGNMENT_TYPES.map(value => ({ label: this.format(value), value }));
  readonly readinessStatusOptions = HR_READINESS_STATUSES.map(value => ({ label: this.format(value), value }));
  readonly severityOptions = HR_READINESS_SEVERITIES.map(value => ({ label: this.format(value), value }));
  workerCategoryOptions: { label: string; value: string }[] = [];
  readonly qualificationStatusOptions = HR_QUALIFICATION_STATUSES.map(value => ({ label: this.format(value), value }));
  readonly booleanOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  ngOnInit(): void {
    this.loadWorkerCategories();
    this.loadEmployees();
    this.loadDrivers();
    this.load();
  }

  private loadWorkerCategories(): void {
    this.hrService.workerCategories().subscribe(response => {
      this.workerCategoryOptions = (response.data ?? []).map(item => ({
        label: item.categoryName || this.format(item.categoryCode), value: item.categoryCode || ''
      }));
    });
  }

  get snapshots(): HrWorkforceReadinessSnapshot[] {
    return this.dashboard.snapshots || [];
  }

  get rules(): HrWorkforceReadinessRule[] {
    return this.dashboard.rulesList || [];
  }

  get overrides(): HrReadinessOverride[] {
    return this.dashboard.overrides || [];
  }

  get qualifications(): HrDriverQualification[] {
    return this.dashboard.qualifications || [];
  }

  get employeeOptions(): { label: string; value: number | undefined }[] {
    return this.employees.map(employee => ({
      label: this.employeeLabel(employee),
      value: employee.employeeId
    }));
  }

  get driverOptions(): { label: string; value: number | undefined }[] {
    return this.drivers.map(driver => ({
      label: this.driverLabel(driver),
      value: driver.id
    }));
  }

  get selectedSnapshotRuleResults(): HrWorkforceReadinessRuleResult[] {
    return this.selectedSnapshot ? this.ruleResults(this.selectedSnapshot) : [];
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Ready Workers', value: this.dashboard.readyWorkers ?? 0, tone: 'success' },
      { label: 'Warning Workers', value: this.dashboard.warningWorkers ?? 0, tone: (this.dashboard.warningWorkers ?? 0) > 0 ? 'warn' : 'success' },
      { label: 'Blocked Workers', value: this.dashboard.blockedWorkers ?? 0, tone: (this.dashboard.blockedWorkers ?? 0) > 0 ? 'danger' : 'success' },
      { label: 'Overrides', value: this.dashboard.activeOverrides ?? this.dashboard.overriddenWorkers ?? 0, tone: (this.dashboard.activeOverrides ?? 0) > 0 ? 'warn' : 'info' },
      { label: 'Rules', value: this.dashboard.rules ?? 0, tone: 'info' },
      { label: 'Driver Qualifications', value: this.dashboard.driverQualifications ?? 0 },
      { label: 'Qualified Drivers', value: this.dashboard.qualifiedDrivers ?? 0, tone: 'success' },
      { label: 'Expired Qualifications', value: this.dashboard.expiredDriverQualifications ?? 0, tone: (this.dashboard.expiredDriverQualifications ?? 0) > 0 ? 'danger' : 'success' }
    ];
  }

  load(): void {
    this.loading = true;
    this.hrService.readinessDashboard(undefined, this.filterAssignmentType, this.filterReadinessStatus).subscribe({
      next: response => this.dashboard = response.data || {},
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Readiness', detail: 'Unable to load workforce readiness.' }),
      complete: () => this.loading = false
    });
  }

  loadEmployees(): void {
    this.hrService.employees({ owningCompanyId: 250, pageSize: 500 }).subscribe({
      next: response => this.employees = response.items || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Readiness', detail: 'Unable to load employee dropdown.' })
    });
  }

  loadDrivers(): void {
    this.driverService.getFreelanceDriversByCompanyId(0, 500, 250).subscribe({
      next: response => this.drivers = response.items || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Readiness', detail: 'Unable to load driver dropdown.' })
    });
  }

  evaluate(): void {
    if (!this.canManageReadiness) return;
    this.loading = true;
    const request = this.cleanedEvaluationRequest();
    this.hrService.evaluateReadiness(request).subscribe({
      next: response => {
        this.evaluationResult = response.data;
        const evaluated = response.data?.evaluatedEmployees ?? 0;
        this.messageService.add({ severity: 'success', summary: 'HR Readiness', detail: `${evaluated} worker readiness snapshots evaluated.` });
        this.activeDialog = undefined;
        this.load();
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'HR Readiness', detail: 'Unable to evaluate workforce readiness.' });
      }
    });
  }

  clearFilters(): void {
    this.filterAssignmentType = undefined;
    this.filterReadinessStatus = undefined;
    this.load();
  }

  resetEvaluation(): void {
    this.evaluationRequest = this.emptyEvaluation();
    this.evaluationResult = undefined;
  }

  editRule(rule: HrWorkforceReadinessRule): void {
    this.ruleForm = { ...rule };
    this.activeDialog = 'rule';
  }

  saveRule(): void {
    if (!this.canManageReadiness) return;
    if (!this.isValidJson(this.ruleForm.ruleConfigJson)) {
      this.messageService.add({ severity: 'warn', summary: 'HR Readiness', detail: 'Rule config JSON is not valid.' });
      return;
    }
    this.hrService.saveReadinessRule(this.ruleForm).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Readiness', detail: 'Readiness rule saved.' });
        this.resetRule();
        this.activeDialog = undefined;
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Readiness', detail: 'Unable to save readiness rule.' })
    });
  }

  resetRule(): void {
    this.ruleForm = this.emptyRule();
  }

  saveOverride(): void {
    if (!this.canOverrideReadiness) return;
    const request: HrReadinessOverride = {
      ...this.overrideForm,
      validUntil: this.toIsoDateTime(this.overrideValidUntilLocal)
    };
    this.hrService.saveReadinessOverride(request).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Readiness', detail: 'Readiness override created.' });
        this.resetOverride();
        this.activeDialog = undefined;
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Readiness', detail: 'Unable to create readiness override.' })
    });
  }

  revokeOverride(override: HrReadinessOverride): void {
    if (!this.canOverrideReadiness || !override.readinessOverrideId) return;
    const reason = `Revoked from HR readiness workspace at ${new Date().toISOString()}`;
    this.hrService.revokeReadinessOverride(override.readinessOverrideId, reason).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Readiness', detail: 'Readiness override revoked.' });
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Readiness', detail: 'Unable to revoke readiness override.' })
    });
  }

  resetOverride(): void {
    this.overrideForm = this.emptyOverride();
    this.overrideValidUntilLocal = this.defaultOverrideUntil();
  }

  editQualification(qualification: HrDriverQualification): void {
    this.qualificationForm = { ...qualification };
    this.activeDialog = 'qualification';
  }

  saveQualification(): void {
    if (!this.canManageQualifications) return;
    this.hrService.saveDriverQualification(this.qualificationForm).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Readiness', detail: 'Driver qualification saved.' });
        this.resetQualification();
        this.activeDialog = undefined;
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Readiness', detail: 'Unable to save driver qualification.' })
    });
  }

  resetQualification(): void {
    this.qualificationForm = this.emptyQualification();
  }

  openActionDialog(dialog: NonNullable<HrWorkforceReadinessComponent['activeDialog']>): void {
    this.activeDialog = dialog;
  }

  actionDialogVisible(dialog: NonNullable<HrWorkforceReadinessComponent['activeDialog']>): boolean {
    return this.activeDialog === dialog;
  }

  setActionDialogVisible(dialog: NonNullable<HrWorkforceReadinessComponent['activeDialog']>, visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  openSnapshot(snapshot: HrWorkforceReadinessSnapshot): void {
    this.selectedSnapshot = snapshot;
    this.snapshotDialogVisible = true;
  }

  ruleResults(snapshot: HrWorkforceReadinessSnapshot): HrWorkforceReadinessRuleResult[] {
    if (!snapshot.ruleResultsJson) return [];
    try {
      const parsed = JSON.parse(snapshot.ruleResultsJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  readinessSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'READY') return 'success';
    if (status === 'WARNING') return 'warn';
    if (status === 'BLOCKED') return 'danger';
    if (status === 'OVERRIDDEN') return 'info';
    return 'secondary';
  }

  qualificationSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'QUALIFIED' || status === 'WAIVED') return 'success';
    if (status === 'PENDING') return 'warn';
    if (status === 'REJECTED' || status === 'EXPIRED') return 'danger';
    return 'secondary';
  }

  ruleResultSeverity(result: HrWorkforceReadinessRuleResult): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (result.blocker || result.resultStatus === 'FAIL') return 'danger';
    if (result.warning || result.resultStatus === 'WARN') return 'warn';
    if (result.resultStatus === 'PASS') return 'success';
    if (result.resultStatus === 'OVERRIDDEN') return 'info';
    return 'secondary';
  }

  format(value?: string): string {
    return (value || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  private employeeLabel(employee: HrEmployee): string {
    const name = employee.displayName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Employee';
    const number = employee.employeeNumber || employee.employeeId || '-';
    const department = employee.departmentName ? ` - ${employee.departmentName}` : '';
    return `${name} (${number})${department}`;
  }

  private driverLabel(driver: Driver): string {
    const name = driver.name || driver.legalName || 'Driver';
    const license = driver.licenseNumber ? ` - ${driver.licenseNumber}` : '';
    return `${name} (#${driver.id || '-'})${license}`;
  }

  private emptyEvaluation(): HrWorkforceReadinessEvaluationRequest {
    return {
      assignmentType: 'SHIPMENT_DELIVERY',
      sourceModule: 'HR_PORTAL',
      forceRefresh: true
    };
  }

  private emptyRule(): HrWorkforceReadinessRule {
    return {
      ruleCode: '',
      ruleName: '',
      assignmentType: 'GENERAL',
      severity: 'BLOCKER',
      ruleConfigJson: '{}',
      cacheTtlMinutes: 15,
      active: true,
      systemDefault: false
    };
  }

  private emptyOverride(): HrReadinessOverride {
    return {
      assignmentType: 'SHIPMENT_DELIVERY',
      overrideReadinessStatus: 'OVERRIDDEN',
      reason: ''
    };
  }

  private emptyQualification(): HrDriverQualification {
    return {
      qualificationStatus: 'QUALIFIED',
      qualificationType: 'DRIVER_QUALIFICATION',
      qualificationName: '',
      active: true,
      sourceModule: 'HR'
    };
  }

  private cleanedEvaluationRequest(): HrWorkforceReadinessEvaluationRequest {
    const request = { ...this.evaluationRequest };
    request.employeeId = this.toNumberOrUndefined(request.employeeId);
    request.driverId = this.toNumberOrUndefined(request.driverId);
    request.assignmentResourceId = this.toNumberOrUndefined(request.assignmentResourceId);
    request.sourceModule = request.sourceModule || 'HR_PORTAL';
    return request;
  }

  private toNumberOrUndefined(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  private isValidJson(value?: string): boolean {
    if (!value) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }

  private defaultOverrideUntil(): string {
    const value = new Date();
    value.setHours(value.getHours() + 8);
    value.setMinutes(0, 0, 0);
    return this.toDatetimeLocal(value);
  }

  private toDatetimeLocal(value: Date): string {
    const offset = value.getTimezoneOffset();
    const local = new Date(value.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  }

  private toIsoDateTime(value?: string): string | undefined {
    if (!value) return undefined;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }
}
